import { useQuery } from '@tanstack/react-query';
import { admin } from '@repletoryreach/sdk-api';
import { Card, Stack, Title, Text, Badge, Group, Skeleton } from '@repletoryreach/sdk-ui';
import { Alert, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { Link } from 'react-router-dom';
import { useT } from '../../../../i18n/I18nProvider';

const MUTED = 'var(--rr-color-text-muted)';

export function IntegrationsTab({ tenantId }: { tenantId: string }) {
  const t = useT();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'integrations', 'org', tenantId],
    queryFn: () => admin.listIntegrations({ scope: 'org', org_id: tenantId })
  });

  if (isLoading) {
    return (
      <Card withBorder>
        <Stack gap="sm"><Skeleton height={20} /><Skeleton height={20} /><Skeleton height={20} /></Stack>
      </Card>
    );
  }
  // `red` is not a mapped Alert colour, so the tab's only error rendered unstyled.
  if (error) return <Alert color="error">{t('tenants.view.integrations.loadError', { message: (error as Error).message })}</Alert>;
  const integrations = data?.data ?? [];

  return (
    <Stack gap="lg">
      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between" align="baseline" gap="sm" wrap="wrap">
            <Title order={4}>{t('tenants.view.integrations.title')}</Title>
            {/* `/integrations` is not a registered route; the console mounts this at /settings/integrations. */}
            <Link to="/settings/integrations" style={{ color: 'var(--rr-color-primary)', fontSize: 13, textDecoration: 'none' }}>
              {t('tenants.view.integrations.manageAll')}
            </Link>
          </Group>
          <Text fz="xs" c={MUTED}>
            {t('tenants.view.integrations.hintOrgScope')}
          </Text>
          {integrations.length === 0 ? (
            <Text fz="sm" c={MUTED}>{t('tenants.view.integrations.empty')}</Text>
          ) : (
            <Stack gap="sm">
              {integrations.map((integ) => (
                <Group key={integ.provider_code} gap="sm" align="baseline" wrap="nowrap">
                  <StatusBadge
                    size="xs"
                    status={
                      integ.last_test_status === 'ok'
                        ? 'healthy'
                        : integ.last_test_status === 'error'
                          ? 'down'
                          : 'inactive'
                    }
                  >
                    {integ.last_test_status === 'ok'
                      ? t('status.ok')
                      : integ.last_test_status === 'error'
                        ? t('status.error')
                        : t('tenants.view.integrations.neverTested')}
                  </StatusBadge>
                  <Text fz="sm" style={{ flex: 1, minWidth: 0 }}>
                    <code>{integ.provider_code}</code>
                    {integ.notes ? <> · {integ.notes}</> : null}
                  </Text>
                  <Badge size="xs" variant={integ.enabled ? 'filled' : 'outline'}>
                    {integ.enabled ? t('tenants.view.integrations.status.enabled') : t('tenants.view.integrations.status.disabled')}
                  </Badge>
                </Group>
              ))}
            </Stack>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
